import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import {
  MonitorPlay, Maximize, Minimize, SlidersHorizontal, X, Search, RefreshCw,
  Check, ChevronDown, ChevronUp, Clock, Activity, Bell, BellRing,
} from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);

/**
 * VMS NOC Wall — Business / Admin only.
 * Auto-refreshing grid of NZTA Variable Message Sign boards. Operator selects which
 * signs appear; selection + layout persist in localStorage so a wall TV survives reloads.
 * Smart-TV safe: solid opaque backgrounds only, explicit scroll buttons in the picker.
 */
const LAYOUTS = {
  4: { cols: 2, label: '2 × 2' },
  6: { cols: 3, label: '3 × 2' },
  8: { cols: 4, label: '4 × 2' },
  9: { cols: 3, label: '3 × 3' },
  12: { cols: 4, label: '4 × 3' },
};
const REFRESH_MS = 30000;
const LS_SELECTED = 'ptt_vms_selected';
const LS_LAYOUT = 'ptt_vms_layout';

function loadSelected() {
  try { const v = JSON.parse(localStorage.getItem(LS_SELECTED) || '[]'); return Array.isArray(v) ? v.map(String) : []; }
  catch { return []; }
}
function loadLayout() {
  const n = parseInt(localStorage.getItem(LS_LAYOUT) || '6', 10);
  return LAYOUTS[n] ? n : 6;
}

export default function VmsNoc() {
  const [signs, setSigns] = useState([]);
  const [selected, setSelected] = useState(loadSelected);
  const [layout, setLayout] = useState(loadLayout);
  const [picker, setPicker] = useState(false);
  const [now, setNow] = useState(dayjs());
  const [lastSync, setLastSync] = useState(null);
  const [full, setFull] = useState(false);
  const [delays, setDelays] = useState([]);
  const [watch, setWatch] = useState([]);

  useEffect(() => { api.get('/vms/watch').then((r) => setWatch(r.data?.ids || [])).catch(() => {}); }, []);
  const toggleWatch = useCallback((id) => {
    setWatch((cur) => {
      const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
      api.put('/vms/watch', { ids: next }).catch(() => {});
      return next;
    });
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/vms');
      setSigns(data || []);
      setLastSync(dayjs());
    } catch { /* keep last */ }
  }, []);
  const loadDelays = useCallback(async () => {
    try { const { data } = await api.get('/journeys/delays?min_delay_s=120'); setDelays(data || []); }
    catch { /* noop */ }
  }, []);

  useEffect(() => { load(); loadDelays(); }, [load, loadDelays]);
  useEffect(() => {
    const iv = setInterval(() => { load(); loadDelays(); }, REFRESH_MS);
    return () => clearInterval(iv);
  }, [load, loadDelays]);
  useEffect(() => { const t = setInterval(() => setNow(dayjs()), 1000); return () => clearInterval(t); }, []);

  useEffect(() => { localStorage.setItem(LS_SELECTED, JSON.stringify(selected)); }, [selected]);
  useEffect(() => { localStorage.setItem(LS_LAYOUT, String(layout)); }, [layout]);

  const byId = useMemo(() => Object.fromEntries(signs.map((s) => [s.id, s])), [signs]);
  const shown = useMemo(() => selected.map((id) => byId[id]).filter(Boolean).slice(0, layout), [selected, byId, layout]);

  const toggle = (id) => setSelected((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);

  const toggleFull = () => {
    if (!document.fullscreenElement) { document.documentElement.requestFullscreen?.(); setFull(true); }
    else { document.exitFullscreen?.(); setFull(false); }
  };

  const cols = LAYOUTS[layout].cols;

  return (
    <div className="min-h-screen text-white flex flex-col" style={{ background: '#050505' }} data-testid="vms-wall">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 flex-shrink-0" style={{ borderBottom: '1px solid #1f1f1f', background: '#0a0a0a' }}>
        <div className="flex items-center gap-3">
          <img src="/brand/logo-icon@2x.png" alt="PTT" className="w-9 h-9 rounded-lg" />
          <div>
            <div className="font-display font-black text-xl leading-none tracking-tight">PTT NZ ALERTS · VMS WALL</div>
            <div className="text-[10px] tracking-[0.3em] uppercase mt-1" style={{ color: '#ffb000' }}>Waka Kotahi NZTA Message Signs</div>
          </div>
        </div>
        <div className="flex items-center gap-2 md:gap-3">
          <div className="flex items-center gap-2 text-sm font-semibold px-3 py-1.5 rounded-lg" style={{ background: '#0f2a1a', color: '#34d399' }} data-testid="vms-online">
            <Activity className="w-4 h-4" /> {signs.length} SIGNS
          </div>
          <div className="text-right leading-none">
            <div className="font-display font-black text-2xl tabular-nums">{now.format('HH:mm:ss')}</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-400">{now.format('ddd D MMM')}</div>
          </div>
          <div className="hidden md:flex items-center gap-1 rounded-lg p-1" style={{ background: '#141414' }}>
            {Object.keys(LAYOUTS).map((n) => (
              <button key={n} onClick={() => setLayout(parseInt(n, 10))} data-testid={`vms-layout-${n}`}
                className="px-2.5 py-1 rounded text-sm font-bold" style={layout === parseInt(n, 10) ? { background: '#ffb000', color: '#111' } : { color: '#a1a1aa' }}>{n}</button>
            ))}
          </div>
          <button onClick={() => setPicker(true)} className="flex items-center gap-2 px-3 py-2 rounded-lg font-semibold text-sm" style={{ background: '#ffb000', color: '#111' }} data-testid="vms-open-picker">
            <SlidersHorizontal className="w-4 h-4" /> Signs ({selected.length})
          </button>
          <button onClick={() => load()} className="p-2 rounded-lg" style={{ background: '#141414' }} data-testid="vms-refresh" title="Refresh"><RefreshCw className="w-4 h-4" /></button>
          <button onClick={toggleFull} className="p-2 rounded-lg" style={{ background: '#141414' }} data-testid="vms-fullscreen" title="Fullscreen">{full ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}</button>
        </div>
      </div>

      {/* Grid */}
      <div className="flex-1 min-h-0 p-3 md:p-4 overflow-hidden">
        {shown.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: '#111' }}>
              <MonitorPlay className="w-8 h-8" style={{ color: '#ffb000' }} />
            </div>
            <div className="font-display font-black text-2xl mb-1">VMS Wall</div>
            <p className="text-zinc-400 max-w-sm mb-5">Pick the electronic message signs you want to watch. Boards update automatically every 30 seconds.</p>
            <button onClick={() => setPicker(true)} className="flex items-center gap-2 px-5 py-3 rounded-lg font-semibold" style={{ background: '#ffb000', color: '#111' }} data-testid="vms-choose">
              <SlidersHorizontal className="w-4 h-4" /> Choose signs
            </button>
          </div>
        ) : (
          <div className="grid gap-3 h-full" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gridAutoRows: '1fr' }}>
            {shown.map((s) => <VmsBoard key={s.id} sign={s} onRemove={() => toggle(s.id)} />)}
          </div>
        )}
      </div>

      {/* Delays ticker */}
      {delays.length > 0 && (
        <div className="flex items-center gap-3 px-4 py-2 flex-shrink-0 overflow-hidden" style={{ background: '#160d0d', borderTop: '1px solid #3a1a1a' }} data-testid="vms-delays">
          <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider flex-shrink-0" style={{ color: '#f87171' }}>
            <Clock className="w-3.5 h-3.5" /> Delays
          </span>
          <div className="flex gap-6 overflow-x-auto whitespace-nowrap text-sm" style={{ scrollbarWidth: 'none' }}>
            {delays.slice(0, 20).map((d) => (
              <span key={d.id} className="flex-shrink-0">
                <b style={{ color: '#ffb000' }}>{d.highway || '—'}</b> <span className="text-zinc-300">{d.leg_name}</span>
                <span className="ml-1.5 font-semibold" style={{ color: '#f87171' }}>+{Math.round(d.delay_s / 60)} min</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Picker */}
      {picker && (
        <SignPicker signs={signs} selected={selected} onToggle={toggle} onClose={() => setPicker(false)}
          onClear={() => setSelected([])} lastSync={lastSync} watch={watch} onToggleWatch={toggleWatch} />
      )}

      <div className="px-4 py-1.5 text-center text-[11px] flex-shrink-0" style={{ background: '#0a0a0a', color: '#52525b' }}>
        <Link to="/noc" className="hover:text-zinc-300">← NOC overview</Link> · Data © Waka Kotahi NZTA · Signs & delays refresh every 30s
      </div>
    </div>
  );
}

function VmsBoard({ sign, onRemove }) {
  const blank = !sign.message;
  return (
    <div className="relative rounded-xl overflow-hidden flex flex-col" style={{ background: '#0d0d0d', border: '1px solid #1c1c1c' }} data-testid={`vms-tile-${sign.id}`}>
      <div className="flex items-center justify-between px-3 py-1.5 flex-shrink-0" style={{ background: '#141414' }}>
        <div className="min-w-0">
          <span className="font-bold text-sm truncate">{sign.highway ? `${sign.highway} · ` : ''}{sign.name}</span>
          {sign.direction && <span className="ml-2 text-[10px] uppercase tracking-wider text-zinc-500">{sign.direction}</span>}
        </div>
        <button onClick={onRemove} className="p-1 rounded hover:bg-zinc-700 text-zinc-400 flex-shrink-0" data-testid={`vms-remove-${sign.id}`}><X className="w-4 h-4" /></button>
      </div>
      {/* LED board */}
      <div className="flex-1 flex items-center justify-center p-4 text-center" style={{ background: blank ? '#0a0a0a' : '#0b0e08' }}>
        {blank ? (
          <span className="text-zinc-600 text-sm uppercase tracking-[0.3em]">— No message —</span>
        ) : (
          <pre className="whitespace-pre-wrap font-mono font-extrabold uppercase leading-tight m-0"
            style={{ color: '#ffb000', textShadow: '0 0 8px rgba(255,176,0,0.45)', fontSize: 'clamp(14px, 2vw, 30px)', letterSpacing: '0.04em' }}>
{sign.message}
          </pre>
        )}
      </div>
    </div>
  );
}

function SignPicker({ signs, selected, onToggle, onClose, onClear, lastSync, watch, onToggleWatch }) {
  const [q, setQ] = useState('');
  const [island, setIsland] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const listRef = useRef(null);

  const filtered = useMemo(() => signs.filter((s) => {
    if (island && s.island !== island) return false;
    if (activeOnly && s.blank) return false;
    if (q) {
      const t = `${s.name} ${s.description} ${s.highway} ${s.message}`.toLowerCase();
      if (!t.includes(q.toLowerCase())) return false;
    }
    return true;
  }), [signs, q, island, activeOnly]);

  const scroll = (dir) => { listRef.current?.scrollBy({ top: dir * 260, behavior: 'smooth' }); };

  return (
    <div className="fixed inset-0 z-50 flex" style={{ background: 'rgba(0,0,0,0.7)' }} data-testid="vms-picker">
      <div className="ml-auto h-full w-full max-w-lg flex flex-col" style={{ background: '#0d0d0d', borderLeft: '1px solid #222' }}>
        <div className="flex items-center justify-between px-5 py-4 flex-shrink-0" style={{ borderBottom: '1px solid #1c1c1c' }}>
          <div>
            <div className="font-display font-black text-lg">Choose signs</div>
            <div className="text-xs text-zinc-500">{selected.length} selected{lastSync ? ` · synced ${lastSync.format('HH:mm:ss')}` : ''}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg" style={{ background: '#1a1a1a' }} data-testid="vms-picker-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-5 py-3 space-y-3 flex-shrink-0" style={{ borderBottom: '1px solid #1c1c1c' }}>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: '#161616' }}>
            <Search className="w-4 h-4 text-zinc-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search sign, highway, message…" className="bg-transparent outline-none text-sm w-full text-white" data-testid="vms-search" />
          </div>
          <div className="flex flex-wrap gap-2">
            {[['', 'All'], ['north', 'North'], ['south', 'South']].map(([v, l]) => (
              <button key={v || 'all'} onClick={() => setIsland(v)} className="px-3 py-1.5 rounded-lg text-sm font-semibold" style={island === v ? { background: '#ffb000', color: '#111' } : { background: '#1a1a1a', color: '#a1a1aa' }} data-testid={`vms-island-${v || 'all'}`}>{l}</button>
            ))}
            <button onClick={() => setActiveOnly((v) => !v)} className="px-3 py-1.5 rounded-lg text-sm font-semibold" style={activeOnly ? { background: '#ffb000', color: '#111' } : { background: '#1a1a1a', color: '#a1a1aa' }} data-testid="vms-active-only">
              Active messages only
            </button>
            {selected.length > 0 && <button onClick={onClear} className="px-3 py-1.5 rounded-lg text-sm font-semibold ml-auto" style={{ background: '#2a1414', color: '#f87171' }} data-testid="vms-clear">Clear all</button>}
          </div>
        </div>

        {/* TV-remote scroll buttons */}
        <div className="flex items-center justify-center gap-2 py-1.5 flex-shrink-0" style={{ background: '#0a0a0a' }}>
          <button onClick={() => scroll(-1)} className="px-4 py-1 rounded" style={{ background: '#1a1a1a' }} data-testid="vms-scroll-up"><ChevronUp className="w-5 h-5" /></button>
          <button onClick={() => scroll(1)} className="px-4 py-1 rounded" style={{ background: '#1a1a1a' }} data-testid="vms-scroll-down"><ChevronDown className="w-5 h-5" /></button>
        </div>

        <div ref={listRef} className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5">
          {filtered.length === 0 && <div className="text-center text-zinc-500 py-10 text-sm">No signs match.</div>}
          {filtered.map((s) => {
            const on = selected.includes(s.id);
            const watching = watch?.includes(s.id);
            return (
              <div key={s.id} className="w-full rounded-lg flex items-stretch gap-1"
                style={{ background: on ? '#1a1508' : '#141414', border: on ? '1px solid #ffb000' : '1px solid transparent' }}>
                <button onClick={() => onToggle(s.id)} className="text-left px-3 py-2.5 flex items-start gap-3 flex-1 min-w-0" data-testid={`vms-opt-${s.id}`}>
                  <span className="mt-0.5 w-5 h-5 rounded flex items-center justify-center flex-shrink-0" style={{ background: on ? '#ffb000' : '#222' }}>
                    {on && <Check className="w-3.5 h-3.5 text-black" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold truncate">{s.highway ? `${s.highway} · ` : ''}{s.name}</span>
                    <span className="block text-xs truncate" style={{ color: s.blank ? '#52525b' : '#ffb000' }}>{s.blank ? 'No current message' : s.message.replace(/\n/g, ' · ')}</span>
                  </span>
                </button>
                <button onClick={() => onToggleWatch(s.id)} title={watching ? 'Alert me when this sign changes (on)' : 'Alert me when this sign changes'} className="px-3 flex items-center flex-shrink-0 rounded-r-lg" data-testid={`vms-watch-${s.id}`}>
                  {watching ? <BellRing className="w-4 h-4" style={{ color: '#ffb000' }} /> : <Bell className="w-4 h-4" style={{ color: '#52525b' }} />}
                </button>
              </div>
            );
          })}
        </div>
        <div className="px-5 py-3 flex-shrink-0" style={{ borderTop: '1px solid #1c1c1c' }}>
          <button onClick={onClose} className="w-full py-2.5 rounded-lg font-semibold" style={{ background: '#ffb000', color: '#111' }} data-testid="vms-done">Done</button>
        </div>
      </div>
    </div>
  );
}
